import { useState } from 'react'
import { useOrganization } from '@/contexts/organization-context'
import { useBillingUsage } from '@/hooks/use-billing-usage'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { BillingUpgradeDialog } from '@/components/billing-upgrade-dialog'

const FREE_CEILING = 10_000

/**
 * Navbar billing pill. Rendered in two cases, both leading to the upgrade dialog:
 *
 *  - **Free trial** — shows free records remaining on hover.
 *  - **Paid org over its committed band** — volume beyond the band isn't billed,
 *    so the org is blocked until it approves the next band. Without this the
 *    blocked org would have no route to the approval dialog.
 *
 * Hidden for exempt (invoice) orgs, and for paid orgs comfortably inside their band.
 * Reads /api/billing/usage — the same state the access gate uses.
 */
export function BillingTrialBadge() {
  const { currentOrganization } = useOrganization()
  // Shared React Query hook — same /billing/usage state the gate reads, deduped.
  const usage = useBillingUsage(currentOrganization?.slug)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  if (!usage || !currentOrganization) return null
  if (usage.is_exempt) return null

  const isFree = Boolean(usage.is_free)
  const blocked = Boolean(usage.blocked)
  const needsUpgrade = Boolean(usage.needs_upgrade)

  // Paid and inside the committed band: nothing to show.
  if (!isFree && !needsUpgrade && !blocked) return null

  const cap = usage.tier_cap ?? FREE_CEILING
  const used = Math.max(0, usage.mtd_records) + Math.max(0, usage.mtd_backfill_records)
  const remaining = usage.records_remaining ?? Math.max(cap - used, 0)
  const nearLimit = remaining <= cap * 0.15

  let label: string
  let tooltip: string

  if (isFree) {
    label = blocked ? 'Free trial — Add payment' : 'Free trial'
    tooltip = blocked
      ? `Free trial limit reached (${cap.toLocaleString()} records). Add a payment method to continue.`
      : `${remaining.toLocaleString()} of ${cap.toLocaleString()} free records left`
  } else {
    label = blocked ? 'Upgrade required' : 'Approve upgrade'
    tooltip = blocked
      ? `You've passed your ${usage.committed_tier} band (${cap.toLocaleString()} records). Approve the ${usage.required_tier} band to continue.`
      : `Volume is approaching your ${usage.committed_tier} band limit (${remaining.toLocaleString()} records left).`
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={blocked ? 'destructive' : 'ghost'}
              size="sm"
              onClick={() => setUpgradeOpen(true)}
              className={!blocked && nearLimit ? 'text-amber-600' : undefined}
            >
              {label}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <BillingUpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        orgSlug={currentOrganization.slug}
        usage={usage}
      />
    </>
  )
}
